import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { CalendarMonth } from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import ItemsPerPageSelector from './ItemsPerPageSelector'
import Pagination from './Pagination'
import { useCountryLaw } from '../context/CountryLawContext'

const SEARCH_TYPE_LABELS = {
  TAX_LAW: 'Tax Law',
  TAX_TRANSPARENCY: 'Tax Transparency',
  TAX_TREATY: 'Tax Treaty',
  UNKNOWN: 'Search',
}

const SEARCH_TYPE_COLORS = {
  TAX_LAW: 'primary',
  TAX_TRANSPARENCY: 'secondary',
  TAX_TREATY: 'success',
  UNKNOWN: 'default',
}

const FILTER_TYPES = [
  { value: 'ALL', label: 'All Types' },
  { value: 'TAX_LAW', label: 'Tax Law' },
  { value: 'TAX_TRANSPARENCY', label: 'Tax Transparency' },
  { value: 'TAX_TREATY', label: 'Tax Treaty' },
]

const datePickerFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.82rem',
    color: '#23252a',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#232536' },
    '&.Mui-focused fieldset': { borderColor: '#232536', borderWidth: '1.5px' },
    '&.Mui-error fieldset': { borderColor: '#e2e8f0' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#3a3e5a' },
}

const filterInputLabelSx = {
  '&.Mui-focused': {
    color: '#232536',
  },
}

const filterSelectSx = {
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#232536',
  },
}

const normalizeValue = (value) => String(value || '').trim().toLowerCase()

const getCountryName = (countryItem) =>
  String(
    countryItem?.countryName || countryItem?.country || countryItem?.name || ''
  ).trim()

const getCountrySlug = (countryItem) =>
  String(countryItem?.countrySlug || countryItem?.slug || getCountryName(countryItem)).trim()

const normalizeLawOption = (lawItem) => {
  if (!lawItem || typeof lawItem !== 'object') {
    return null
  }

  const value =
    lawItem?.lawShortName || lawItem?.shortName || lawItem?.slug || lawItem?.id || ''
  const label =
    lawItem?.lawFullName || lawItem?.lawName || lawItem?.title || lawItem?.name || value

  if (!value) {
    return null
  }

  return {
    value: String(value).trim(),
    label: String(label || value).trim(),
  }
}

const toJsDate = (value) => {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'string') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0] =
      value
    if (!year || !month || !day) {
      return null
    }

    const parsed = new Date(
      Date.UTC(
        year,
        month - 1,
        day,
        hour,
        minute,
        second,
        Math.floor((Number(nanosecond) || 0) / 1000000)
      )
    )
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  return null
}

const formatTimestamp = (timestamp) => {
  const parsed = toJsDate(timestamp)
  if (!parsed) {
    return 'N/A'
  }

  return parsed.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const resolveScopeText = (activity, lawNameLookup = new Map()) => {
  if (!activity) {
    return 'N/A'
  }

  const type = activity.searchType || 'UNKNOWN'
  if (type === 'TAX_TREATY') {
    const primary = activity.country1 || '-'
    const partner = activity.country2 || 'All Partners'
    return `${primary} / ${partner}`
  }

  if (type === 'TAX_LAW' || type === 'TAX_TRANSPARENCY') {
    const country = activity.country || '-'
    const lawShortName = activity.law || '-'
    const lookupKey = `${normalizeValue(country)}::${normalizeValue(lawShortName)}`
    const lawDisplayName =
      lawNameLookup.get(lookupKey) || String(lawShortName || '-').trim() || '-'
    return `${country} / ${lawDisplayName}`
  }

  return activity.country || activity.country1 || '-'
}

const isLikelyTruncated = (text, threshold) =>
  typeof text === 'string' && text.trim().length > threshold

const SearchActivityTab = ({
  activities = [],
  loading = false,
  totalItems = 0,
  totalPages = 0,
  currentPage = 1,
  itemsPerPage = 10,
  filters = {},
  onPageChange,
  onItemsPerPageChange,
  onFiltersChange,
  onOpenSearch,
}) => {
  const { countriesWithLaws = [], refreshCountriesData } = useCountryLaw()
  const [dateDialogOpen, setDateDialogOpen] = useState(false)
  const [draftDateFrom, setDraftDateFrom] = useState(filters?.dateFrom || '')
  const [draftDateTo, setDraftDateTo] = useState(filters?.dateTo || '')

  useEffect(() => {
    if (countriesWithLaws.length === 0 && typeof refreshCountriesData === 'function') {
      refreshCountriesData()
    }
  }, [countriesWithLaws.length, refreshCountriesData])

  const hasPagination = useMemo(
    () => totalItems > itemsPerPage && totalPages > 1,
    [itemsPerPage, totalItems, totalPages]
  )

  const activeType = filters?.type || 'ALL'
  const activeCountry = filters?.country || ''
  const activeLaw = filters?.law || ''
  const activeDateFrom = filters?.dateFrom || ''
  const activeDateTo = filters?.dateTo || ''
  const isLawFilterEnabled = Boolean(activeCountry) && activeType !== 'TAX_TREATY'

  const countryOptions = useMemo(() => {
    const uniqueCountries = new Map()

    countriesWithLaws.forEach((countryItem) => {
      const countryName = getCountryName(countryItem)
      if (!countryName) {
        return
      }

      const key = normalizeValue(countryName)
      if (!uniqueCountries.has(key)) {
        uniqueCountries.set(key, {
          value: countryName,
          label: countryName,
          sortKey: getCountrySlug(countryItem) || countryName,
        })
      }
    })

    return Array.from(uniqueCountries.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    )
  }, [countriesWithLaws])

  const selectedCountryData = useMemo(
    () =>
      countriesWithLaws.find(
        (item) => normalizeValue(getCountryName(item)) === normalizeValue(activeCountry)
      ) || null,
    [countriesWithLaws, activeCountry]
  )

  const availableLawOptions = useMemo(() => {
    if (!selectedCountryData) {
      return []
    }

    const countryLaws = selectedCountryData?.laws || []
    const countryTaxTransparencies = selectedCountryData?.taxTransparencies || []
    let rawList = []

    if (activeType === 'TAX_TRANSPARENCY') {
      rawList = countryTaxTransparencies
    } else if (activeType === 'TAX_LAW') {
      rawList = countryLaws
    } else if (activeType === 'ALL') {
      rawList = [...countryLaws, ...countryTaxTransparencies]
    }

    const deduped = new Map()

    rawList.map(normalizeLawOption).filter(Boolean).forEach((lawOption) => {
      const key = normalizeValue(lawOption.value)
      if (!deduped.has(key)) {
        deduped.set(key, lawOption)
      }
    })

    return Array.from(deduped.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    )
  }, [activeType, selectedCountryData])

  const lawNameLookup = useMemo(() => {
    const mapping = new Map()

    countriesWithLaws.forEach((countryItem) => {
      const countryName = getCountryName(countryItem)
      if (!countryName) {
        return
      }

      const lawGroups = [countryItem?.laws, countryItem?.taxTransparencies]
      lawGroups.forEach((lawList) => {
        if (!Array.isArray(lawList)) {
          return
        }

        lawList.map(normalizeLawOption).filter(Boolean).forEach((lawOption) => {
          const mapKey = `${normalizeValue(countryName)}::${normalizeValue(lawOption.value)}`
          mapping.set(mapKey, lawOption.label)
        })
      })
    })

    return mapping
  }, [countriesWithLaws])

  const hasActiveFilters = Boolean(
    (activeType && activeType !== 'ALL') ||
      activeCountry ||
      activeLaw ||
      activeDateFrom ||
      activeDateTo
  )

  const handleOpenSearch = (searchPath) => {
    if (!searchPath || typeof onOpenSearch !== 'function') {
      return
    }
    onOpenSearch(searchPath)
  }

  const updateFilters = (nextPartialFilters) => {
    if (typeof onFiltersChange !== 'function') {
      return
    }
    onFiltersChange({
      ...filters,
      ...nextPartialFilters,
    })
  }

  const handleTypeChange = (event) => {
    const nextType = event.target.value
    updateFilters({
      type: nextType,
      law: nextType === 'TAX_TREATY' ? '' : activeLaw,
    })
  }

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value
    updateFilters({
      country: nextCountry,
      law: '',
    })
  }

  const handleClearFilters = () => {
    updateFilters({
      type: 'ALL',
      country: '',
      law: '',
      dateFrom: '',
      dateTo: '',
    })
  }

  const handleOpenDateDialog = () => {
    setDraftDateFrom(activeDateFrom)
    setDraftDateTo(activeDateTo)
    setDateDialogOpen(true)
  }

  const handleApplyDateRange = () => {
    updateFilters({
      dateFrom: draftDateFrom || '',
      dateTo: draftDateTo || '',
    })
    setDateDialogOpen(false)
  }

  const handleResetDateRange = () => {
    setDraftDateFrom('')
    setDraftDateTo('')
  }

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1.5}
        >
          <Box>
            <Typography variant='h6' sx={{ color: '#232536' }}>
              Search Activity
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Legend: A = Articles, D = Decisions, S = Supplementary publications
            </Typography>
          </Box>
          <ItemsPerPageSelector
            itemsPerPage={itemsPerPage}
            setItemsPerPage={onItemsPerPageChange}
            loading={loading}
            options={[10, 20, 30]}
          />
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          useFlexGap
          flexWrap='wrap'
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <FormControl size='small' sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel id='search-activity-type-filter-label' sx={filterInputLabelSx}>
              Type
            </InputLabel>
            <Select
              labelId='search-activity-type-filter-label'
              value={activeType}
              label='Type'
              onChange={handleTypeChange}
              MenuProps={{ disableScrollLock: true }}
              sx={filterSelectSx}
            >
              {FILTER_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel id='search-activity-country-filter-label' sx={filterInputLabelSx}>
              Country
            </InputLabel>
            <Select
              labelId='search-activity-country-filter-label'
              value={activeCountry}
              label='Country'
              onChange={handleCountryChange}
              MenuProps={{ disableScrollLock: true }}
              sx={filterSelectSx}
            >
              <MenuItem value=''>All Countries</MenuItem>
              {countryOptions.map((countryItem) => (
                <MenuItem key={countryItem.value} value={countryItem.value}>
                  {countryItem.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size='small'
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
            disabled={!isLawFilterEnabled}
          >
            <InputLabel id='search-activity-law-filter-label' sx={filterInputLabelSx}>
              Law
            </InputLabel>
            <Select
              labelId='search-activity-law-filter-label'
              value={activeLaw}
              label='Law'
              onChange={(event) => updateFilters({ law: event.target.value })}
              MenuProps={{ disableScrollLock: true }}
              sx={filterSelectSx}
            >
              <MenuItem value=''>All Laws</MenuItem>
              {availableLawOptions.map((lawOption) => (
                <MenuItem key={lawOption.value} value={lawOption.value}>
                  {lawOption.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            onClick={handleOpenDateDialog}
            startIcon={<CalendarMonth sx={{ fontSize: 17 }} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              border: '1px solid',
              px: 1.8,
              height: '40px',
              whiteSpace: 'nowrap',
              borderColor:
                activeDateFrom || activeDateTo ? '#23252a' : '#e2e8f0',
              color: activeDateFrom || activeDateTo ? 'white' : '#23252a',
              backgroundColor:
                activeDateFrom || activeDateTo ? '#23252a' : 'white',
              '&:hover': {
                backgroundColor:
                  activeDateFrom || activeDateTo ? '#374151' : '#f9fafb',
                borderColor: '#23252a',
              },
            }}
          >
            Date Range
          </Button>

          {hasActiveFilters && (
            <Button
              onClick={handleClearFilters}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: '40px',
                px: 1.8,
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                border: '1px solid #fca5a5',
                color: '#dc2626',
                backgroundColor: '#fff5f5',
                '&:hover': { backgroundColor: '#fee2e2', borderColor: '#dc2626' },
              }}
            >
              Clear All
            </Button>
          )}
        </Stack>
      </Stack>

      <Dialog
        open={dateDialogOpen}
        onClose={() => setDateDialogOpen(false)}
        maxWidth='xs'
        fullWidth
        disableScrollLock
        PaperProps={{ sx: { borderRadius: '12px', mx: { xs: 2, sm: 'auto' } } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>
            Filter by Date Range
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, px: 2.5, pb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={1.5}>
              <Box>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                  From
                </Typography>
                <DatePicker
                  value={draftDateFrom ? dayjs(draftDateFrom) : null}
                  onChange={(value) =>
                    setDraftDateFrom(value ? value.format('YYYY-MM-DD') : '')
                  }
                  format='DD/MM/YYYY'
                  maxDate={draftDateTo ? dayjs(draftDateTo) : undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      placeholder: 'DD/MM/YYYY',
                      sx: datePickerFieldSx,
                    },
                    popper: { disableScrollLock: true },
                  }}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                  To
                </Typography>
                <DatePicker
                  value={draftDateTo ? dayjs(draftDateTo) : null}
                  onChange={(value) =>
                    setDraftDateTo(value ? value.format('YYYY-MM-DD') : '')
                  }
                  format='DD/MM/YYYY'
                  minDate={draftDateFrom ? dayjs(draftDateFrom) : undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      placeholder: 'DD/MM/YYYY',
                      sx: datePickerFieldSx,
                    },
                    popper: { disableScrollLock: true },
                  }}
                />
              </Box>
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1 }}>
          <Button
            onClick={handleResetDateRange}
            size='small'
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '6px',
              border: '1px solid #23252a',
              color: '#23252a',
              px: 2,
              '&:hover': {
                borderColor: '#23252a',
                color: '#23252a',
                backgroundColor: '#f1f5f9',
              },
            }}
          >
            Reset
          </Button>
          <Button
            onClick={handleApplyDateRange}
            variant='contained'
            size='small'
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '6px',
              backgroundColor: '#23252a',
              color: 'white',
              px: 2.5,
              '&:hover': { backgroundColor: '#374151' },
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='center'
          spacing={1.5}
          sx={{ py: 6 }}
        >
          <CircularProgress size={28} sx={{ color: '#232536' }} />
          <Typography variant='body2' color='text.secondary'>
            Loading search activity...
          </Typography>
        </Stack>
      ) : activities.length === 0 ? (
        <Alert severity='info' sx={{ borderRadius: 2 }}>
          No search activity found yet.
        </Alert>
      ) : (
        <>
          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
            <Table size='small' sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Query</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Results</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Breakdown</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity) => {
                  const searchType = activity?.searchType || 'UNKNOWN'
                  const queryValue = String(activity?.query || '').trim()
                  const hasMeaningfulQuery =
                    Boolean(queryValue) && normalizeValue(queryValue) !== 'no query'
                  const totalResults = Number(activity?.totalResults || 0)
                  const articles = Number(activity?.articlesCount || 0)
                  const decisions = Number(activity?.decisionsCount || 0)
                  const guidelines = Number(activity?.guidelinesCount || 0)
                  const scopeText = resolveScopeText(activity, lawNameLookup)
                  const hasBreakdown =
                    searchType === 'TAX_LAW' || searchType === 'TAX_TRANSPARENCY'
                  const showArticles = articles > 0
                  const showDecisions = decisions > 0
                  const showSupplements = guidelines > 0
                  const hasVisibleBreakdown =
                    showArticles || showDecisions || showSupplements
                  const showScopeTooltip = isLikelyTruncated(scopeText, 22)
                  const showQueryTooltip = isLikelyTruncated(queryValue, 24)

                  return (
                    <TableRow key={activity?.id || `${searchType}-${queryValue}-${activity?.timestamp}`}>
                      <TableCell>
                        <Chip
                          size='small'
                          label={
                            SEARCH_TYPE_LABELS[searchType] ||
                            SEARCH_TYPE_LABELS.UNKNOWN
                          }
                          color={
                            SEARCH_TYPE_COLORS[searchType] ||
                            SEARCH_TYPE_COLORS.UNKNOWN
                          }
                          variant='outlined'
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Tooltip
                          title={showScopeTooltip ? scopeText : ''}
                          arrow
                          placement='bottom'
                          disableFocusListener={!showScopeTooltip}
                          disableHoverListener={!showScopeTooltip}
                          disableTouchListener={!showScopeTooltip}
                        >
                          <Typography
                            variant='body2'
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontFamily: 'monospace',
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                            }}
                          >
                            {scopeText}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        {hasMeaningfulQuery ? (
                          <Tooltip
                            title={showQueryTooltip ? queryValue : ''}
                            arrow
                            placement='bottom'
                            disableFocusListener={!showQueryTooltip}
                            disableHoverListener={!showQueryTooltip}
                            disableTouchListener={!showQueryTooltip}
                          >
                            <Box sx={{ maxWidth: 210 }}>
                              <Button
                                size='small'
                                sx={{
                                  textTransform: 'none',
                                  maxWidth: 210,
                                  justifyContent: 'flex-start',
                                  px: 0,
                                }}
                                onClick={() =>
                                  handleOpenSearch(activity?.searchPath)
                                }
                                disabled={!activity?.searchPath}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 200,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {queryValue}
                                </Typography>
                              </Button>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant='body2' color='text.secondary'>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{totalResults}</Typography>
                      </TableCell>
                      <TableCell>
                        {hasBreakdown && hasVisibleBreakdown ? (
                          <Stack
                            direction='row'
                            spacing={0.75}
                            flexWrap='wrap'
                            useFlexGap
                          >
                            {showArticles && (
                              <Chip size='small' label={`A: ${articles}`} />
                            )}
                            {showDecisions && (
                              <Chip size='small' label={`D: ${decisions}`} />
                            )}
                            {showSupplements && (
                              <Chip size='small' label={`S: ${guidelines}`} />
                            )}
                          </Stack>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatTimestamp(activity?.timestamp)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {hasPagination && (
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              currentPage={currentPage}
              paginate={onPageChange}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default SearchActivityTab
